import React from "react";

export const BUILD_INFO = {
  id: "ui-fidelity-2026-06-19T05-20Z",
  label: "UI fidelity build 2026-06-19 05:20 UTC",
};

const NAV_ICON_PATHS = {
  overview: "M4 5h6v6H4zM14 5h6v6h-6zM4 15h6v4H4zM14 15h6v4h-6z",
  scenarios: "M5 6l7-3 7 3-7 3zM5 12l7-3 7 3-7 3zM5 18l7-3 7 3-7 3z",
  solar: "M12 5v14M5 12h14M7.1 7.1l9.8 9.8M16.9 7.1l-9.8 9.8M12 8a4 4 0 100 8 4 4 0 000-8z",
  bodies: "M12 3a9 9 0 100 18 9 9 0 000-18zM5 12h14M12 3c3 4 3 14 0 18M12 3c-3 4-3 14 0 18",
  process: "M8 4h8l-1 6 4 7a3 3 0 01-2.6 4H7.6A3 3 0 015 17l4-7zM9 10h6",
  claims: "M7 3h10l3 3v15H4V6zM7 8h10M7 12h10M7 16h6",
  formula: "M5 6h14M7 12h10M9 18h6M8 6c1 4 1 8 0 12M16 6c-1 4-1 8 0 12",
  asteroids: "M12 4l3 2 4 1 1 4-2 3-1 4-5 2-4-2-3-1-1-5 2-4 3-2z",
  sources: "M5 4h11a3 3 0 013 3v13H8a3 3 0 01-3-3zM8 4v16M9 8h6M9 12h7",
  packages: "M4 7l8-4 8 4-8 4zM4 12l8 4 8-4M4 17l8 4 8-4",
  roadmap: "M5 19V5h14v14zM8 8h4M8 12h8M8 16h5M15 8l2 2-2 2",
  approval: "M4 12l5 5L20 6M4 20h16",
};

export function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export function downloadJson(filename, payload) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function StatusPill({ tone = "neutral", children }) {
  return <span className={`status-pill status-${tone}`}>{children}</span>;
}

export function ActionButton({ children, onClick, variant = "default", className = "", type = "button" }) {
  const buttonClass = [variant === "primary" ? "primary-button" : "", className].filter(Boolean).join(" ");
  return (
    <button className={buttonClass || undefined} type={type} onClick={onClick}>
      {children}
    </button>
  );
}

export function JsonExportButton({
  filename,
  payload,
  createPayload,
  children,
  className = "ghost-button",
  variant = "default",
}) {
  function handleClick() {
    const resolvedFilename = typeof filename === "function" ? filename() : filename;
    const resolvedPayload = createPayload ? createPayload() : payload;
    downloadJson(resolvedFilename, resolvedPayload);
  }

  return (
    <ActionButton className={className} onClick={handleClick} variant={variant}>
      {children}
    </ActionButton>
  );
}

export function ButtonRow({ actions, className = "button-row" }) {
  return (
    <div className={className}>
      {actions.map((action) => (
        <ActionButton key={action.label} onClick={action.onClick} variant={action.variant} className={action.className}>
          {action.label}
        </ActionButton>
      ))}
    </div>
  );
}

export function ReviewActionRow({ target, recordApproval, actions, className = "button-row" }) {
  return (
    <ButtonRow
      className={className}
      actions={actions.map((action) => ({
        ...action,
        onClick: () => recordApproval(target, action.action),
      }))}
    />
  );
}

export function ProgressBar({ value, tone = "blue" }) {
  return (
    <span className="metric-bar-wrap">
      <span className="metric-bar" style={{ width: `${clamp(value, 0, 100)}%` }} data-tone={tone} />
    </span>
  );
}

export function ScoreBarList({ rows, className = "matrix-bars" }) {
  return (
    <div className={className}>
      {rows.map((row) => (
        <React.Fragment key={row.label}>
          <Metric label={row.label} value={row.valueLabel ?? ""} />
          <ProgressBar value={row.value} tone={row.tone} />
        </React.Fragment>
      ))}
    </div>
  );
}

export function ScoreBarCell({ value, tone = "blue", label }) {
  return (
    <span className="cell-with-bar">
      <ProgressBar value={value} tone={tone} />
      <small>{label ?? value}</small>
    </span>
  );
}

function SolarLogoMark() {
  return (
    <svg className="logo-svg" viewBox="0 0 64 64" aria-hidden="true">
      <path d="M32 4l24 14v28L32 60 8 46V18z" />
      <circle cx="32" cy="32" r="10" />
      <path d="M32 13v8M32 43v8M13 32h8M43 32h8M18.5 18.5l5.7 5.7M39.8 39.8l5.7 5.7M45.5 18.5l-5.7 5.7M24.2 39.8l-5.7 5.7" />
    </svg>
  );
}

export function NavGlyph({ id }) {
  const path = NAV_ICON_PATHS[id] || NAV_ICON_PATHS.overview;
  return (
    <svg className="nav-svg" viewBox="0 0 24 24" aria-hidden="true">
      <path d={path} />
    </svg>
  );
}

export function StatusGlyph({ tone }) {
  const iconPath = {
    green: "M4 12l5 5L20 6",
    amber: "M12 4v9M12 18h.01M4 20h16L12 4z",
    blue: "M4 12a8 8 0 111.8 5.1M4 17v-5h5",
    red: "M12 4l8 4v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V8zM9 9l6 6M15 9l-6 6",
  }[tone] || "M4 12h16M12 4v16";
  return (
    <span className={`status-glyph tone-${tone}`} aria-hidden="true">
      <svg viewBox="0 0 24 24">
        <path d={iconPath} />
      </svg>
    </span>
  );
}

export function AppShellLogo() {
  return (
    <div className="logo-wrap" aria-label="Interstellar Manufacturing Planner">
      <div className="logo-mark" aria-hidden="true">
        <SolarLogoMark />
      </div>
      <div className="logo-copy">
        <p>INNER SOLAR</p>
        <strong>INDUSTRIAL PLANNER</strong>
        <small>BETA</small>
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, muted }) {
  return (
    <section className="page-hero">
      <div>
        <p className="eyebrow">{muted}</p>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
    </section>
  );
}

export function Metric({ label, value }) {
  return (
    <article className="metric-cell">
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

export function MetricGrid({ metrics, columns = 4, className = "" }) {
  const gridClass = [`metric-grid-${columns}`, className].filter(Boolean).join(" ");
  return (
    <div className={gridClass}>
      {metrics.map((metric) => (
        <Metric key={metric.label} label={metric.label} value={metric.value} />
      ))}
    </div>
  );
}

export function EvidencePanel({ title, status, tone = "amber", className = "", children }) {
  const panelClass = ["panel", className].filter(Boolean).join(" ");
  return (
    <article className={panelClass}>
      <div className="panel-topline">
        <h2>{title}</h2>
        {status ? <StatusPill tone={tone}>{status}</StatusPill> : null}
      </div>
      {children}
    </article>
  );
}

export function CellStack({ title, meta, children }) {
  return (
    <span className="cell-stack">
      <strong>{title}</strong>
      {meta ? <span>{meta}</span> : null}
      {children}
    </span>
  );
}

export function StackList({
  items,
  getKey = (item, index) => item.id ?? item.key ?? index,
  getTitle,
  getMeta,
  getBody,
  className = "stack-list",
}) {
  return (
    <div className={className}>
      {items.map((item, index) => (
        <div className="stack-item" key={getKey(item, index)}>
          <strong>{getTitle(item, index)}</strong>
          {getMeta ? <span>{getMeta(item, index)}</span> : null}
          {getBody ? <p>{getBody(item, index)}</p> : null}
        </div>
      ))}
    </div>
  );
}

export function PillSelector({
  items,
  selectedId,
  onSelect,
  getItemId = (item) => item.id,
  getItemLabel = (item) => item.name,
  className = "pill-bar",
}) {
  return (
    <div className={className}>
      {items.map((item) => {
        const id = getItemId(item);
        return (
          <button
            key={id}
            className={id === selectedId ? "pill pill-active" : "pill"}
            onClick={() => onSelect(id)}
            type="button"
          >
            {getItemLabel(item)}
          </button>
        );
      })}
    </div>
  );
}

export function DataTable({
  columns,
  rows,
  getRowKey,
  compact = false,
  shell = "scroll",
  className = "",
  getRowClassName,
  onRowClick,
}) {
  const wrapperClass = shell === "shell" ? "table-shell" : shell === "event" ? "event-table-wrap" : "table-scroll";
  const tableClass = ["matrix-table", compact ? "compact" : "", className].filter(Boolean).join(" ");

  return (
    <div className={wrapperClass}>
      <table className={tableClass}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={column.headerClassName}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={getRowKey ? getRowKey(row, rowIndex) : row.id ?? row.key ?? rowIndex}
              className={getRowClassName ? getRowClassName(row, rowIndex) : undefined}
              onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
            >
              {columns.map((column) => (
                <td key={column.key} className={column.cellClassName?.(row, rowIndex)}>
                  {column.render ? column.render(row, rowIndex) : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
